// src/pages/MedicationReconciliation/MedicationReconciliationComp/useReconciliation.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeAISuggestion, reconcileHeuristics } from '../Data/factories';
import { getClinicalPatients } from '../../../utils/services/Patients/getClinicalPatients';
import { matchAdvancedMdPatientAbility } from '../../../utils/services/AdvancedMD/advancedMdMatchAbility';
import { getPatientMedications } from '../../../utils/services/AdvancedMD/medications';

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

/**
 * Separa "LASTNAME, FIRSTNAME" en { firstName, lastName }.
 * Si no hay coma, asume que todo es lastName.
 */
function parseNameFromMemberFullName(fullName = '') {
  const parts = fullName.split(',').map((s) => s.trim());
  if (parts.length >= 2) {
    return { lastName: parts[0], firstName: parts[1] };
  }
  return { lastName: parts[0] || '', firstName: '' };
}

export function useReconciliation() {
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // Paginación (fallback 600 si el API no devuelve total)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(600);
  const [totalPages, setTotalPages] = useState(0);

  // Filtros API
  const [filters, setFilters] = useState({
    memberFullName: null,
    filterAdmitted: null,
    filterDischarged: null,
    filterEmergency: null,
    filterHighCost: null,
    filterHighRisk: null,
  });

  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [activeMeds, setActiveMeds] = useState([]);
  const [externalMeds, setExternalMeds] = useState([]);
  const [reconciliationItems, setReconciliationItems] = useState([]);

  const [notes, setNotes] = useState('');

  const [loadingPatient, setLoadingPatient] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [message, setMessage] = useState(null);

  // AdvancedMD match state
  const [advancedMdMatch, setAdvancedMdMatch] = useState(null);
  const [advancedMdPatientId, setAdvancedMdPatientId] = useState(null);

  // Set de keys editadas manualmente — preserva edits durante auto-reconcile
  const editedKeysRef = useRef(new Set());

  const getPatientById = useCallback(
    (pid) => patients.find((p) => p.id === pid) || null,
    [patients]
  );

  // --- DataGrid paginationModel (0-based) ---
  const paginationModel = useMemo(
    () => ({ page: page - 1, pageSize }),
    [page, pageSize]
  );

  const handlePaginationModelChange = useCallback((model) => {
    setPage(model.page + 1); // DataGrid 0-based → API 1-based
    setPageSize(model.pageSize);
  }, []);

  // --- Fetch patients from API ---
  const fetchPatients = useCallback(async (overrides = {}) => {
    setLoadingPatients(true);
    try {
      const res = await getClinicalPatients({
        ...filters,
        pageNumber: page,
        pageSize,
        ...overrides,
      });
      setPatients(res.data.map(p => ({
        ...p,
        id: p.cipId,
        name: p.memberFullName,
        document: p.memberId,
      })));
      // Usar total del API si existe, sino mantener fallback 600
      if (res.total != null && res.total > 0) setTotal(res.total);
      if (res.totalPages != null) setTotalPages(res.totalPages);
    } catch (err) {
      console.error("Error fetching clinical patients:", err);
    } finally {
      setLoadingPatients(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  const applyFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1);
  }, []);

  const changePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  // --- Auto-reconcile: corre heurísticas cuando cambian activeMeds o externalMeds ---
  // Solo ejecuta cuando hay al menos 1 external med (evita marcar todo como STOP
  // cuando solo existen activos y aún no se agregan externos).
  useEffect(() => {
    if (!selectedPatientId) return;
    if (externalMeds.length === 0) return;
    const heuristicItems = reconcileHeuristics(activeMeds, externalMeds);
    setReconciliationItems((prev) => {
      const edited = editedKeysRef.current;
      if (edited.size === 0) return heuristicItems;
      // Preservar items editados manualmente
      const editedMap = new Map();
      for (const item of prev) {
        if (edited.has(item.key)) editedMap.set(item.key, item);
      }
      return heuristicItems.map((item) =>
        editedMap.has(item.key) ? editedMap.get(item.key) : item
      );
    });
  }, [activeMeds, externalMeds, selectedPatientId]);

  // --- Select patient: match AdvancedMD + load medications ---
  const selectPatient = useCallback(async (patient) => {
    if (!patient) return;
    const pid = patient.cipId || patient.id;
    setSelectedPatientId(pid);
    setLoadingPatient(true);
    setMessage(null);
    setAdvancedMdMatch(null);
    setAdvancedMdPatientId(null);
    setActiveMeds([]);
    setExternalMeds([]);
    setReconciliationItems([]);
    setNotes('');
    editedKeysRef.current = new Set();

    try {
      // 1. Parse name (memberFullName viene como "LASTNAME, FIRSTNAME")
      const { firstName, lastName } = parseNameFromMemberFullName(patient.memberFullName || patient.name);
      const rawDob = patient.dob || patient.dateOfBirth || patient.birthDate || null;
      const sex = patient.gender || patient.sex || '';

      // Convertir DOB a ISO-8601 (mismo formato que PatientMapPage)
      let dobIso = null;
      if (rawDob) {
        const d = new Date(rawDob);
        if (!Number.isNaN(d.getTime())) dobIso = d.toISOString();
      }

      // 2. Match against AdvancedMD (misma lógica que PatientMapPage)
      const matchPayload = { firstName, lastName, dob: dobIso, sex };
      let matchResult = null;
      let amdPatientId = null;
      try {
        matchResult = await matchAdvancedMdPatientAbility(matchPayload);
        setAdvancedMdMatch(matchResult);

        // Extraer candidate.id de la respuesta
        let bestMatch = null;
        if (matchResult?.candidate) {
          bestMatch = matchResult;
        } else if (Array.isArray(matchResult) && matchResult.length > 0) {
          bestMatch = matchResult[0];
        } else if (matchResult?.matches && matchResult.matches.length > 0) {
          bestMatch = matchResult.matches[0];
        } else if (matchResult?.bestMatch) {
          bestMatch = matchResult;
        }

        if (bestMatch) {
          const candidate = bestMatch.candidate ?? bestMatch;
          const rawId = candidate?.id || candidate?.patientId || null;
          if (rawId) {
            // Normalizar: quitar prefijo "pat" si existe
            const normalized = String(rawId).startsWith('pat') ? String(rawId).slice(3) : String(rawId);
            amdPatientId = normalized;
          }
        }
      } catch (matchErr) {
        console.error("AdvancedMD match failed:", matchErr);
        setAdvancedMdMatch({ error: true, message: matchErr.message });
      }

      setAdvancedMdPatientId(amdPatientId);

      // 3. If match found, load medications from AdvancedMD
      if (amdPatientId) {
        try {
          const medsData = await getPatientMedications(amdPatientId);
          const allMeds = Array.isArray(medsData) ? medsData : (medsData?.data || medsData?.medications || []);

          // Solo medicamentos activos (currentMedFlag === true)
          const currentMeds = allMeds.filter((m) => m.currentMedFlag === true);

          const mapped = currentMeds.map((m) => ({
            id: m.id,
            patientId: m.patientId || pid,
            name: m.drugName || '',
            drugStrength: m.drugStrength || '',
            doseForm: m.doseForm || '',
            route: m.route || '',
            frequency: m.frequency || '',
            startDate: m.startDate || null,
            stopDate: m.stopDate || null,
            doctor: m.doctor || '',
            refills: m.refills ?? 0,
            quantity: m.quantity ?? 0,
            duration: m.duration ?? 0,
            status: m.status || '',
            prescType: m.prescType || '',
            deaclassDesc: m.deaclassDesc || '',
            source: 'AdvancedMD',
          }));
          setActiveMeds(mapped);
        } catch (medErr) {
          console.error("Error loading medications:", medErr);
          setMessage({ type: 'warning', text: 'Could not load medications from AdvancedMD.' });
        }
      } else {
        setMessage({ type: 'info', text: 'No AdvancedMD match found for this patient.' });
      }
    } catch (err) {
      console.error("Error selecting patient:", err);
      setMessage({ type: 'error', text: 'Error loading patient data.' });
    } finally {
      setLoadingPatient(false);
    }
  }, []);

  // AI genera sugerencias y las aplica directo al board
  const generateAISuggestions = useCallback(async () => {
    if (!selectedPatientId) return;
    setLoadingAI(true);
    setMessage(null);
    await delay(900);

    const suggestion = makeAISuggestion(selectedPatientId, activeMeds, externalMeds);
    setReconciliationItems(suggestion.items);
    editedKeysRef.current = new Set();
    setLoadingAI(false);
    setMessage({ type: 'success', text: 'AI suggestions applied to the board.' });
  }, [selectedPatientId, activeMeds, externalMeds]);

  const updateItemAction = useCallback((key, action) => {
    editedKeysRef.current.add(key);
    setReconciliationItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, action } : it))
    );
  }, []);

  const updateItemTarget = useCallback((key, partialTo) => {
    editedKeysRef.current.add(key);
    setReconciliationItems((prev) =>
      prev.map((it) =>
        it.key === key ? { ...it, to: { ...(it.to || {}), ...(partialTo || {}) } } : it
      )
    );
  }, []);

  const setItemReason = useCallback((key, text) => {
    editedKeysRef.current.add(key);
    setReconciliationItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, reason: text } : it))
    );
  }, []);

  const setItemNote = useCallback((key, text) => {
    setReconciliationItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, note: text } : it))
    );
  }, []);

  // Simplificado: solo agrega a externalMeds, auto-reconcile se encarga del board
  const addExternalPrescription = useCallback((prescription) => {
    if (!selectedPatientId) return;

    const newEntry = {
      patientId: selectedPatientId,
      id: `EN${Math.floor(Math.random() * 9000) + 1000}`,
      name: prescription.name,
      dose: prescription.dose,
      route: prescription.route,
      frequency: prescription.frequency,
      source: prescription.source || (() => {
        const pt = getPatientById(selectedPatientId);
        if (pt?.isEmergency) return 'EMERGENCY';
        if (pt?.isDischarged) return 'DISCHARGED';
        return 'ADMITTED';
      })(),
    };

    setExternalMeds((prev) => [...prev, newEntry]);
    setMessage({ type: 'success', text: 'External prescription added.' });
  }, [selectedPatientId, getPatientById]);

  const confirmReconciliation = useCallback(async () => {
    await delay(500);
    setMessage({ type: 'success', text: 'Reconciliation confirmed (mock).' });
  }, []);

  const selectedPatient = useMemo(
    () => (selectedPatientId ? getPatientById(selectedPatientId) : null),
    [selectedPatientId, getPatientById]
  );

  const summary = useMemo(() => {
    const counters = {
      CONTINUE: 0,
      MODIFY: 0,
      STOP: 0,
      NEW: 0,
    };
    for (const it of reconciliationItems) {
      if (counters[it.action] !== undefined) counters[it.action] += 1;
    }
    return {
      total: reconciliationItems.length,
      ...counters,
      items: reconciliationItems,
    };
  }, [reconciliationItems]);

  const reset = useCallback(() => {
    setSelectedPatientId(null);
    setActiveMeds([]);
    setExternalMeds([]);
    setReconciliationItems([]);
    setNotes('');
    setLoadingAI(false);
    setLoadingPatient(false);
    setMessage(null);
    setAdvancedMdMatch(null);
    setAdvancedMdPatientId(null);
    editedKeysRef.current = new Set();
  }, []);

  return {
    patients,
    selectedPatient,
    selectedPatientId,
    activeMeds,
    externalMeds,
    reconciliationItems,
    loadingPatient,
    loadingPatients,
    loadingAI,
    message,
    notes,
    setNotes,
    selectPatient,
    generateAISuggestions,
    updateItemAction,
    updateItemTarget,
    setItemNote,
    setItemReason,
    addExternalPrescription,
    confirmReconciliation,
    reset,
    summary,
    // Pagination & filters
    page,
    total,
    totalPages,
    filters,
    applyFilters,
    changePage,
    fetchPatients,
    paginationModel,
    handlePaginationModelChange,
    // AdvancedMD
    advancedMdMatch,
    advancedMdPatientId,
  };
}
